import { SECTION_CARD } from '@/components/ui/section-card-variants'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import Skeleton from '@/components/ui/Skeleton'
import { cn } from '@/lib/utils'

/*
 * Brokers · By stock before its first response: the LOADED page, block for
 * block, in its order and at its heights — the same rule Market's own
 * `MarketBoardStates.tsx` sets out.
 *
 * Every block reuses the loaded component's own box classes — the same
 * padding, gaps and text-line boxes, and `DataTableSkeleton` for the three
 * tables, which is the loaded table's own panel — so it matches at every width,
 * not just a measured one:
 *
 *   controls row       the stock picker + the period chips and calendar button
 *   price head         symbol/name over the LTP pill
 *   window note        one line
 *   most bought table  36 + 12 + a `DataTableSkeleton` (head band + 4 two-line rows)
 *   most sold table    same shape
 *   who's left holding same shape
 *   bar panel          a `SectionCard` — title, caption, 4 bars
 *   pv-range link      one line
 *   footnote           one line
 *
 * The three tables' row count is data-dependent (each is server-ranked top
 * 8, `StockBrokerTable`/`StockHolderTable`'s own `pageSize={8}`) — 4 is the
 * typical count this measured against, not a ceiling; a fuller table only
 * grows past this skeleton's height, it never shrinks under it.
 *
 * Bars only, no words, so nothing here can be mistaken for, or found by a
 * test as, the loaded content.
 */

/** A bar vertically centred in a text line box of `line` height. */
function Line({ line, bar }: { line: string; bar: string }) {
  return (
    <div className={cn('flex items-center', line)}>
      <Skeleton className={cn('rounded-xs', bar)} />
    </div>
  )
}

/** The stock picker + period chips and calendar button — `ByStockScreen`'s own top row. */
function ControlsSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Skeleton className="h-11 w-48 rounded-sm lg:h-9" />
      <Skeleton className="h-11 w-72 rounded-sm lg:h-9" />
    </div>
  )
}

/** PriceHead: symbol heading + company name, LTP and its change pill. */
function PriceHeadSkeleton() {
  return (
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
      <div className="min-w-0">
        <Line line="h-8" bar="h-6 w-28 rounded-sm" />
        <Line line="h-4" bar="h-3 w-40 rounded-xs" />
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <Skeleton className="h-6 w-20 rounded-sm" />
        <Skeleton className="h-6 w-16 rounded-xs" />
      </div>
    </div>
  )
}

/**
 * One `StockBrokerTable` / `StockHolderTable`: a `SectionHead size="card"` row (36px, the same
 * 16px title as the bar panel's), 12px, then the table at its own `rowHeight="lg"` (56px — the
 * Broker column is two lines, code over name).
 */
function StockTableSkeleton({ title }: { title: string }) {
  return (
    <div className="flex min-w-0 flex-col gap-3">
      <Line line="h-9" bar={cn('h-4', title)} />
      <DataTableSkeleton rows={4} columns={5} rowHeight="lg" />
    </div>
  )
}

/** `LeftHoldingBars`: its card's head, the one-line caption, then four bar rows. */
function BarsSkeleton() {
  return (
    <div className={SECTION_CARD}>
      <div className="flex min-h-9 items-center">
        <Skeleton className="h-4 w-48 rounded-xs" />
      </div>
      <div className="mt-3">
        <Line line="h-4" bar="h-3 w-80 max-w-full" />
        <div className="mt-4 flex flex-col gap-4">
          {[0, 1, 2, 3].map((row) => (
            <div key={row} className="flex flex-col gap-2">
              <Line line="h-5" bar="h-4 w-40" />
              <Skeleton className="h-2 w-full rounded-none" />
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

/** Everything under the controls row: what the screen draws while its own read is out. */
export function ByStockBodySkeleton() {
  return (
    <>
      <PriceHeadSkeleton />
      <Line line="h-4" bar="h-3 w-32 rounded-xs" />
      <StockTableSkeleton title="w-24" />
      <StockTableSkeleton title="w-20" />
      <StockTableSkeleton title="w-48" />
      <BarsSkeleton />
      <Line line="h-6" bar="h-4 w-56 rounded-xs" />
      <Line line="h-4" bar="h-3 w-full max-w-md rounded-xs" />
    </>
  )
}

export function ByStockSkeleton() {
  return (
    <>
      <ControlsSkeleton />
      <ByStockBodySkeleton />
    </>
  )
}
